import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  Save,
  LogOut,
  RotateCcw,
  Home as HomeIcon,
  Cpu,
  Boxes,
  FlaskConical,
  Sparkles,
  Image as ImageIcon,
  Settings,
  ExternalLink,
  Loader2,
  Video,
  Upload,
  History,
  Undo2,
  Eye,
  EyeOff,
  Trash2,
  RefreshCw,
  Search,
  MapPin,
} from "lucide-react";
import {
  fetchContent,
  saveContent,
  resetContent,
  uploadFile,
  absoluteMediaUrl,
  listVersions,
  restoreVersion,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { buildDefaultContentFromI18n } from "@/lib/defaultContent";
import i18n from "@/i18n";

// ---- Mock-mode helpers (used when running against no backend, e.g. Netlify demo) ----
const MOCK_TOKEN_PREFIX = "mock-admin-";
const isMockMode = () =>
  (localStorage.getItem("pergamon_admin_token") || "").startsWith(MOCK_TOKEN_PREFIX);

// In-memory version snapshots for mock mode
const mockVersions = [];
const mockNextId = () => `mock-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const nowIso = () => new Date().toISOString();

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

// ---- helpers ----
const clone = (v) => JSON.parse(JSON.stringify(v));
const setPath = (obj, path, value) => {
  const next = clone(obj);
  let cur = next;
  for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]];
  cur[path[path.length - 1]] = value;
  return next;
};

const LANGS = [
  { code: "tr", label: "Türkçe" },
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
];

const SECTIONS = [
  { id: "general", label: "Genel", icon: Settings },
  { id: "home", label: "Ana Sayfa", icon: HomeIcon },
  { id: "tech", label: "Teknoloji", icon: Cpu },
  { id: "hardware", label: "Donanım", icon: Boxes },
  { id: "solutions", label: "Çözümler", icon: FlaskConical },
  { id: "whyus", label: "Neden Biz?", icon: Sparkles },
  { id: "contact", label: "İletişim", icon: MapPin },
  { id: "seo", label: "SEO Meta", icon: Search },
  { id: "media", label: "Medya", icon: ImageIcon },
  { id: "history", label: "Geçmiş", icon: History },
];

// ---- primitive fields ----
const Field = ({ label, value, onChange, placeholder, testId, mono }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest text-[#1a3636]/60 mb-1.5 block">{label}</span>
    <input
      type="text"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className={`w-full px-4 py-2.5 rounded-lg border border-[#1a3636]/15 bg-white text-[#1a3636] focus:outline-none focus:border-[#2b8a7e] focus:ring-2 focus:ring-[#2b8a7e]/15 transition ${mono ? "font-mono text-sm" : ""}`}
    />
  </label>
);

const Area = ({ label, value, onChange, rows = 3, testId }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest text-[#1a3636]/60 mb-1.5 block">{label}</span>
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      data-testid={testId}
      className="w-full px-4 py-2.5 rounded-lg border border-[#1a3636]/15 bg-white text-[#1a3636] focus:outline-none focus:border-[#2b8a7e] focus:ring-2 focus:ring-[#2b8a7e]/15 transition resize-y"
    />
  </label>
);

// ---- Drop-zone media field ----
const MediaField = ({ label, url, onChange, kind, testId }) => {
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const isVideo = kind === "video" || /\.(mp4|webm|mov|m4v)($|\?)/i.test(url || "");
  const previewUrl = absoluteMediaUrl(url);

  const doUpload = useCallback(
    async (file) => {
      if (!file) return;
      setBusy(true);
      setProgress(0);
      try {
        if (isMockMode()) {
          // Local-only demo upload — inline as data URL so preview + storage works without backend
          const dataUrl = await readFileAsDataUrl(file);
          setProgress(100);
          onChange(dataUrl);
          toast.success(`Yerel önizleme: ${file.name} (Demo modu)`);
          return;
        }
        const { url: newUrl } = await uploadFile(file, setProgress);
        onChange(newUrl);
        toast.success(`Yüklendi: ${file.name}`);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Yükleme başarısız.");
      } finally {
        setBusy(false);
        setProgress(0);
      }
    },
    [onChange]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <div className="p-4 rounded-xl border border-[#1a3636]/10 bg-[#f8faf9]">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-widest text-[#1a3636]/70 flex items-center gap-2">
          {isVideo ? <Video size={13} /> : <ImageIcon size={13} />}
          {label}
        </span>
        {previewUrl && (
          <a href={previewUrl} target="_blank" rel="noreferrer" className="text-[11px] text-[#2b8a7e] hover:underline inline-flex items-center gap-1">
            Aç <ExternalLink size={11} />
          </a>
        )}
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !busy && inputRef.current?.click()}
        data-testid={`${testId}-dropzone`}
        role="button"
        tabIndex={0}
        className={`flex gap-3 items-stretch rounded-lg border-2 border-dashed cursor-pointer transition p-3 ${
          dragOver ? "border-[#2b8a7e] bg-[#2b8a7e]/5" : "border-[#1a3636]/15 hover:border-[#2b8a7e]/50 bg-white"
        }`}
      >
        <div className="w-24 h-24 rounded-lg overflow-hidden bg-[#1a3636]/10 flex items-center justify-center flex-shrink-0 relative">
          {previewUrl ? (
            isVideo ? (
              <video src={previewUrl} muted className="w-full h-full object-cover" />
            ) : (
              <img src={previewUrl} alt={label} className="w-full h-full object-cover" />
            )
          ) : (
            <span className="text-xs text-[#1a3636]/40">Boş</span>
          )}
          {busy && (
            <div className="absolute inset-0 bg-black/50 text-white flex items-center justify-center text-xs">
              {progress}%
            </div>
          )}
        </div>
        <div className="flex-1 flex flex-col justify-between">
          <div className="flex items-center gap-2 text-[11px] text-[#1a3636]/60">
            <Upload size={12} />
            {dragOver ? "Bırakın yüklensin" : "Sürükle-bırak veya tıklayıp seçin"}
          </div>
          <input
            type="text"
            value={url ?? ""}
            onChange={(e) => onChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder="https://... veya /api/uploads/..."
            data-testid={testId}
            className="w-full mt-2 px-3 py-2 rounded-lg border border-[#1a3636]/15 bg-white text-[#1a3636] text-xs font-mono focus:outline-none focus:border-[#2b8a7e]"
          />
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        data-testid={`${testId}-file-input`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) doUpload(f);
          e.target.value = "";
        }}
      />
    </div>
  );
};

// ---- section renderers ----
const GeneralSection = ({ content, lang, setField }) => {
  const nav = content.translations[lang].nav;
  const footer = content.translations[lang].footer;
  return (
    <div className="space-y-8">
      <div>
        <h3 className="font-display text-lg font-semibold text-[#1a3636] mb-4">Menü</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Ana Sayfa" value={nav.home} onChange={(v) => setField(["translations", lang, "nav", "home"], v)} testId="admin-nav-home" />
          <Field label="Teknoloji" value={nav.technology} onChange={(v) => setField(["translations", lang, "nav", "technology"], v)} testId="admin-nav-technology" />
          <Field label="Donanım" value={nav.hardware} onChange={(v) => setField(["translations", lang, "nav", "hardware"], v)} testId="admin-nav-hardware" />
          <Field label="Çözümler" value={nav.solutions} onChange={(v) => setField(["translations", lang, "nav", "solutions"], v)} testId="admin-nav-solutions" />
          <Field label="Neden Biz?" value={nav.whyus} onChange={(v) => setField(["translations", lang, "nav", "whyus"], v)} testId="admin-nav-whyus" />
        </div>
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-[#1a3636] mb-4">Footer</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Area label="Tagline" value={footer.tagline} onChange={(v) => setField(["translations", lang, "footer", "tagline"], v)} testId="admin-footer-tagline" />
          <div className="space-y-4">
            <Field label="İletişim başlığı" value={footer.contact} onChange={(v) => setField(["translations", lang, "footer", "contact"], v)} />
            <Field label="Menü başlığı" value={footer.nav} onChange={(v) => setField(["translations", lang, "footer", "nav"], v)} />
            <Field label="Haklar satırı" value={footer.rights} onChange={(v) => setField(["translations", lang, "footer", "rights"], v)} />
            <Field label="E-posta" value={footer.email} onChange={(v) => setField(["translations", lang, "footer", "email"], v)} testId="admin-footer-email" />
          </div>
        </div>
      </div>
    </div>
  );
};

const HomeSection = ({ content, lang, setField }) => {
  const home = content.translations[lang].home;
  return (
    <div className="space-y-8">
      <div>
        <h3 className="font-display text-lg font-semibold text-[#1a3636] mb-4">Hero</h3>
        <div className="grid grid-cols-1 gap-4">
          <Field label="Overline (üst yazı)" value={home.overline} onChange={(v) => setField(["translations", lang, "home", "overline"], v)} testId="admin-home-overline" />
          <div>
            <span className="text-[11px] uppercase tracking-widest text-[#1a3636]/60 mb-1.5 block">Başlık satırları</span>
            {home.heroLines.map((line, i) => (
              <input
                key={i}
                type="text"
                value={line}
                onChange={(e) => {
                  const arr = [...home.heroLines];
                  arr[i] = e.target.value;
                  setField(["translations", lang, "home", "heroLines"], arr);
                }}
                data-testid={`admin-home-heroLine-${i}`}
                className="w-full mb-2 px-4 py-2.5 rounded-lg border border-[#1a3636]/15 bg-white text-[#1a3636] focus:outline-none focus:border-[#2b8a7e] focus:ring-2 focus:ring-[#2b8a7e]/15"
              />
            ))}
          </div>
          <Area label="Alt yazı" value={home.heroSub} onChange={(v) => setField(["translations", lang, "home", "heroSub"], v)} testId="admin-home-heroSub" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Kurumsal buton" value={home.btnCorporate} onChange={(v) => setField(["translations", lang, "home", "btnCorporate"], v)} />
            <Field label="Bireysel buton" value={home.btnIndividual} onChange={(v) => setField(["translations", lang, "home", "btnIndividual"], v)} />
          </div>
        </div>
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-[#1a3636] mb-4">Nasıl Çalışır?</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Başlık" value={home.howTitle} onChange={(v) => setField(["translations", lang, "home", "howTitle"], v)} />
          <Field label="Alt başlık" value={home.howSub} onChange={(v) => setField(["translations", lang, "home", "howSub"], v)} />
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {home.cards.map((c, i) => (
            <div key={i} className="p-4 rounded-xl border border-[#1a3636]/10 bg-[#f8faf9] space-y-3">
              <p className="text-xs uppercase tracking-widest text-[#2b8a7e]">Kart {i + 1}</p>
              <Field label="Başlık" value={c.title} onChange={(v) => setField(["translations", lang, "home", "cards", i, "title"], v)} />
              <Area label="Açıklama" value={c.desc} onChange={(v) => setField(["translations", lang, "home", "cards", i, "desc"], v)} rows={4} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TechSection = ({ content, lang, setField }) => {
  const tech = content.translations[lang].tech;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Overline" value={tech.overline} onChange={(v) => setField(["translations", lang, "tech", "overline"], v)} />
        <Field label="Başlık" value={tech.title} onChange={(v) => setField(["translations", lang, "tech", "title"], v)} />
        <Area label="Alt açıklama" value={tech.sub} onChange={(v) => setField(["translations", lang, "tech", "sub"], v)} />
        <Field label="Diyagram etiketi" value={tech.diagramLabel} onChange={(v) => setField(["translations", lang, "tech", "diagramLabel"], v)} />
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-[#1a3636] mb-4">5 Parametre</h3>
        <Field label="Parametre bölüm başlığı" value={tech.paramsTitle} onChange={(v) => setField(["translations", lang, "tech", "paramsTitle"], v)} />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {tech.params.map((p, i) => (
            <div key={i} className="p-4 rounded-xl border border-[#1a3636]/10 bg-[#f8faf9] space-y-3">
              <p className="text-xs uppercase tracking-widest text-[#2b8a7e]">Parametre {i + 1}</p>
              <Field label="Ad" value={p.name} onChange={(v) => setField(["translations", lang, "tech", "params", i, "name"], v)} />
              <Area label="Açıklama" value={p.desc} onChange={(v) => setField(["translations", lang, "tech", "params", i, "desc"], v)} rows={3} />
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-[#1a3636] mb-4">Vurgu Metrik</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Değer" value={tech.highlight} onChange={(v) => setField(["translations", lang, "tech", "highlight"], v)} />
          <Field label="Etiket" value={tech.highlightLabel} onChange={(v) => setField(["translations", lang, "tech", "highlightLabel"], v)} />
          <Area label="Açıklama" value={tech.highlightDesc} onChange={(v) => setField(["translations", lang, "tech", "highlightDesc"], v)} />
        </div>
      </div>
    </div>
  );
};

const HardwareSection = ({ content, lang, setField }) => {
  const hw = content.translations[lang].hardware;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Overline" value={hw.overline} onChange={(v) => setField(["translations", lang, "hardware", "overline"], v)} />
        <Field label="Başlık" value={hw.title} onChange={(v) => setField(["translations", lang, "hardware", "title"], v)} />
        <Area label="Alt açıklama" value={hw.sub} onChange={(v) => setField(["translations", lang, "hardware", "sub"], v)} />
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-[#1a3636] mb-4">4 Ürün</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {hw.products.map((p, i) => (
            <div key={i} className="p-4 rounded-xl border border-[#1a3636]/10 bg-[#f8faf9] space-y-3">
              <p className="text-xs uppercase tracking-widest text-[#2b8a7e]">Ürün {i + 1}</p>
              <Field label="Ad" value={p.name} onChange={(v) => setField(["translations", lang, "hardware", "products", i, "name"], v)} />
              <Area label="Açıklama" value={p.desc} onChange={(v) => setField(["translations", lang, "hardware", "products", i, "desc"], v)} rows={3} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SolutionsSection = ({ content, lang, setField }) => {
  const s = content.translations[lang].solutions;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Overline" value={s.overline} onChange={(v) => setField(["translations", lang, "solutions", "overline"], v)} />
        <Field label="Başlık" value={s.title} onChange={(v) => setField(["translations", lang, "solutions", "title"], v)} />
        <Area label="Alt açıklama" value={s.sub} onChange={(v) => setField(["translations", lang, "solutions", "sub"], v)} />
        <Field label="Laboratuvar etiketi" value={s.labLabel} onChange={(v) => setField(["translations", lang, "solutions", "labLabel"], v)} />
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-[#1a3636] mb-4">4 Özellik</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {s.items.map((it, i) => (
            <div key={i} className="p-4 rounded-xl border border-[#1a3636]/10 bg-[#f8faf9] space-y-3">
              <p className="text-xs uppercase tracking-widest text-[#2b8a7e]">Özellik {i + 1}</p>
              <Field label="Ad" value={it.name} onChange={(v) => setField(["translations", lang, "solutions", "items", i, "name"], v)} />
              <Area label="Açıklama" value={it.desc} onChange={(v) => setField(["translations", lang, "solutions", "items", i, "desc"], v)} rows={3} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const WhyUsSection = ({ content, lang, setField }) => {
  const w = content.translations[lang].whyus;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Overline" value={w.overline} onChange={(v) => setField(["translations", lang, "whyus", "overline"], v)} />
        <Field label="Başlık" value={w.title} onChange={(v) => setField(["translations", lang, "whyus", "title"], v)} />
        <Field label="Son etiket" value={w.finalLabel} onChange={(v) => setField(["translations", lang, "whyus", "finalLabel"], v)} />
        <Area label="Son alıntı" value={w.finalQuote} onChange={(v) => setField(["translations", lang, "whyus", "finalQuote"], v)} />
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-[#1a3636] mb-4">3 Manifesto Bölümü</h3>
        <div className="grid grid-cols-1 gap-4">
          {w.chapters.map((c, i) => (
            <div key={i} className="p-4 rounded-xl border border-[#1a3636]/10 bg-[#f8faf9] space-y-3">
              <div className="grid grid-cols-4 gap-3">
                <Field label="Numara" value={c.no} onChange={(v) => setField(["translations", lang, "whyus", "chapters", i, "no"], v)} />
                <div className="col-span-3">
                  <Field label="Bölüm başlığı" value={c.title} onChange={(v) => setField(["translations", lang, "whyus", "chapters", i, "title"], v)} />
                </div>
              </div>
              <Area label="Metin" value={c.text} onChange={(v) => setField(["translations", lang, "whyus", "chapters", i, "text"], v)} rows={4} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ContactSection = ({ content, lang, setField }) => {
  const c = content.translations[lang].contact || {};
  const put = (k) => (v) => setField(["translations", lang, "contact", k], v);
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Overline" value={c.overline} onChange={put("overline")} testId="admin-contact-overline" />
        <Field label="Başlık" value={c.title} onChange={put("title")} testId="admin-contact-title" />
        <Area label="Alt açıklama" value={c.sub} onChange={put("sub")} rows={3} />
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-[#1a3636] mb-4">Adres & Telefon</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Adres etiketi" value={c.addressLabel} onChange={put("addressLabel")} />
          <Field label="Telefon etiketi" value={c.phoneLabel} onChange={put("phoneLabel")} />
          <Area label="Adres" value={c.address} onChange={put("address")} testId="admin-contact-address" rows={3} />
          <Field label="Telefon" value={c.phone} onChange={put("phone")} testId="admin-contact-phone" />
        </div>
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-[#1a3636] mb-4">E-postalar</h3>
        <Field label="Bölüm etiketi" value={c.emailsLabel} onChange={put("emailsLabel")} />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: "General", testId: "general" },
            { key: "Admin", testId: "admin" },
            { key: "Sales", testId: "sales" },
          ].map((row) => (
            <div key={row.key} className="p-4 rounded-xl border border-[#1a3636]/10 bg-[#f8faf9] space-y-3">
              <Field label={`${row.key} etiketi`} value={c[`email${row.key}Label`]} onChange={put(`email${row.key}Label`)} />
              <Field label={`${row.key} e-postası`} value={c[`email${row.key}`]} onChange={put(`email${row.key}`)} testId={`admin-contact-email-${row.testId}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SEO_PAGES = [
  { key: "home", label: "Ana Sayfa (/)" },
  { key: "tech", label: "Teknoloji (/teknoloji)" },
  { key: "hardware", label: "Donanım (/donanim)" },
  { key: "solutions", label: "Çözümler (/cozumler)" },
  { key: "whyus", label: "Neden Biz? (/neden-biz)" },
  { key: "contact", label: "İletişim (/iletisim)" },
];

const SeoSection = ({ content, lang, setField }) => {
  const seo = content.translations[lang].seo || {};
  return (
    <div className="space-y-6">
      <p className="text-sm text-[#1a3636]/70">
        Her sayfa için tarayıcı sekmesindeki <b>başlık</b>, arama motorları için <b>açıklama</b> ve sosyal medyada paylaşımda görünecek <b>OG görseli</b>.
      </p>
      {SEO_PAGES.map((p) => {
        const s = seo[p.key] || {};
        const put = (k) => (v) => setField(["translations", lang, "seo", p.key, k], v);
        return (
          <div key={p.key} data-testid={`admin-seo-${p.key}`} className="p-5 rounded-xl border border-[#1a3636]/10 bg-[#f8faf9]">
            <p className="text-xs uppercase tracking-widest text-[#2b8a7e] mb-4">{p.label}</p>
            <div className="grid grid-cols-1 gap-4">
              <Field label="Title (Sekme başlığı)" value={s.title} onChange={put("title")} testId={`admin-seo-${p.key}-title`} />
              <Area label="Description (Meta açıklama)" value={s.description} onChange={put("description")} testId={`admin-seo-${p.key}-description`} rows={3} />
              <MediaField
                label="OG Image (Sosyal medya görseli)"
                kind="image"
                url={s.ogImage}
                onChange={put("ogImage")}
                testId={`admin-seo-${p.key}-ogImage`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MediaSection = ({ content, setField }) => {
  const m = content.media || {};
  const entries = [
    { key: "heroVideo", label: "Ana Sayfa Hero Videosu", kind: "video" },
    { key: "systemDiagram", label: "Teknoloji Sistem Diyagramı", kind: "image" },
    { key: "growbot", label: "Donanım · Growbot System 8i", kind: "image" },
    { key: "multidoser", label: "Donanım · Multidoser", kind: "image" },
    { key: "vacuumDryer", label: "Donanım · Vacuum Fog Dryer", kind: "image" },
    { key: "diyKit", label: "Donanım · DIY Kitler", kind: "image" },
    { key: "lab1", label: "Çözümler · Laboratuvar 1", kind: "image" },
    { key: "lab2", label: "Çözümler · Laboratuvar 2", kind: "image" },
    { key: "nature", label: "Neden Biz? · Doğa & Sağlık", kind: "image" },
  ];
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {entries.map((e) => (
        <MediaField
          key={e.key}
          label={e.label}
          kind={e.kind}
          url={m[e.key]}
          onChange={(v) => setField(["media", e.key], v)}
          testId={`admin-media-${e.key}`}
        />
      ))}
    </div>
  );
};

const HistorySection = ({ onRestored }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isMockMode()) {
        setVersions([...mockVersions].sort((a, b) => (b.snapshot_at || "").localeCompare(a.snapshot_at || "")));
      } else {
        const list = await listVersions();
        setVersions(list);
      }
    } catch (e) {
      toast.error("Geçmiş yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const restore = async (id) => {
    if (!window.confirm("Bu sürüme geri dönmek istediğinize emin misiniz? Mevcut içerik geçmişe eklenecek.")) return;
    setBusyId(id);
    try {
      if (isMockMode()) {
        const idx = mockVersions.findIndex((v) => v.id === id);
        if (idx < 0) throw new Error("not found");
        const target = mockVersions[idx];
        await onRestored?.(target);
      } else {
        await restoreVersion(id);
        await onRestored?.();
      }
      toast.success("Sürüm geri yüklendi.");
      await load();
    } catch (e) {
      toast.error("Geri alma başarısız.");
    } finally {
      setBusyId(null);
    }
  };

  const fmt = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString("tr-TR");
    } catch {
      return iso;
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#1a3636]/70">
          Her <b>Kaydet</b> öncesi mevcut içerik burada saklanır. İstediğiniz sürüme <b>Geri Al</b> ile dönebilirsiniz.
        </p>
        <button
          onClick={load}
          data-testid="admin-history-refresh"
          className="inline-flex items-center gap-1.5 text-xs text-[#1a3636]/70 hover:text-[#2b8a7e] px-3 py-1.5 rounded-full border border-[#1a3636]/10"
        >
          <RefreshCw size={13} /> Yenile
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#1a3636]/60">
          <Loader2 className="animate-spin" size={14} /> Yükleniyor…
        </div>
      ) : versions.length === 0 ? (
        <div data-testid="admin-history-empty" className="text-sm text-[#1a3636]/50 p-8 border border-dashed border-[#1a3636]/15 rounded-xl text-center">
          Henüz kayıtlı sürüm yok. İlk değişikliğinizi kaydettiğinizde burada listelenecek.
        </div>
      ) : (
        <ul data-testid="admin-history-list" className="space-y-2">
          {versions.map((v) => (
            <li
              key={v.id}
              data-testid={`admin-history-item-${v.id}`}
              className="flex items-center justify-between gap-4 p-4 rounded-xl border border-[#1a3636]/10 bg-white"
            >
              <div>
                <p className="text-sm font-medium text-[#1a3636]">{fmt(v.snapshot_at)}</p>
                <p className="text-xs text-[#1a3636]/50">
                  Oluşturan: {v.updated_by} · Orijinal kayıt: {fmt(v.created_at)}
                </p>
              </div>
              <button
                onClick={() => restore(v.id)}
                disabled={busyId === v.id}
                data-testid={`admin-history-restore-${v.id}`}
                className="inline-flex items-center gap-1.5 text-sm bg-[#2b8a7e] text-white px-4 py-2 rounded-full hover:bg-[#1a3636] transition disabled:opacity-60"
              >
                {busyId === v.id ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />}
                Geri Al
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---- main ----
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { refresh } = useContent();
  const [content, setContent] = useState(null);
  const [lang, setLang] = useState("tr");
  const [section, setSection] = useState("general");
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const [previewKey, setPreviewKey] = useState(0);
  const [previewRoute, setPreviewRoute] = useState("/");

  const iframeRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchContent();
        setContent(data);
      } catch (e) {
        // Backend unreachable (e.g. Netlify static deploy). Fall back to bundled defaults.
        setContent(buildDefaultContentFromI18n(i18n));
        if (isMockMode()) {
          toast.info("Demo modu: içerik yerel varsayılanlardan yüklendi.");
        } else {
          toast.error("İçerik yüklenemedi — varsayılanlar kullanıldı.");
        }
      }
    })();
  }, []);

  // When user switches to a section, auto-navigate preview to that section's page
  useEffect(() => {
    const map = {
      general: "/",
      home: "/",
      tech: "/teknoloji",
      hardware: "/donanim",
      solutions: "/cozumler",
      whyus: "/neden-biz",
      contact: "/iletisim",
      seo: previewRoute,
      media: "/",
      history: previewRoute,
    };
    if (map[section] && map[section] !== previewRoute) {
      setPreviewRoute(map[section]);
    }
  }, [section]);

  const setField = (path, value) => setContent((c) => setPath(c, path, value));

  const reloadPreview = () => setPreviewKey((k) => k + 1);

  const save = async () => {
    if (!content) return;
    setBusy(true);
    try {
      const payload = { translations: content.translations, media: content.media };
      if (isMockMode()) {
        // Snapshot for local history, then apply live to i18n
        mockVersions.unshift({
          id: mockNextId(),
          snapshot_at: nowIso(),
          created_at: nowIso(),
          updated_by: "admin@pergamon-agritech.com (demo)",
          translations: JSON.parse(JSON.stringify(payload.translations)),
          media: JSON.parse(JSON.stringify(payload.media)),
        });
        // Apply to i18n so site + preview iframe reflect changes live
        ["tr", "en", "de"].forEach((lng) => {
          if (payload.translations[lng]) {
            i18n.addResourceBundle(lng, "translation", payload.translations[lng], true, true);
          }
        });
        i18n.changeLanguage(i18n.language);
      } else {
        await saveContent(payload);
        await refresh();
      }
      reloadPreview();
      setSavedAt(new Date());
      toast.success(
        isMockMode()
          ? "Kaydedildi (demo modu — sadece bu oturum)."
          : "Kaydedildi. Site anında güncellendi."
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kaydetme başarısız.");
    } finally {
      setBusy(false);
    }
  };

  const doReset = async () => {
    if (!window.confirm("Tüm içeriği varsayılan değerlere geri döndürmek istiyor musunuz?")) return;
    setBusy(true);
    try {
      if (isMockMode()) {
        const fresh = buildDefaultContentFromI18n(i18n);
        setContent(fresh);
        ["tr", "en", "de"].forEach((lng) => {
          if (fresh.translations[lng]) {
            i18n.addResourceBundle(lng, "translation", fresh.translations[lng], true, true);
          }
        });
        i18n.changeLanguage(i18n.language);
      } else {
        await resetContent();
        const fresh = await fetchContent();
        setContent(fresh);
        await refresh();
      }
      reloadPreview();
      toast.success("İçerik varsayılana sıfırlandı.");
    } catch {
      toast.error("Sıfırlama başarısız.");
    } finally {
      setBusy(false);
    }
  };

  const onRestored = async (localTarget) => {
    if (isMockMode() && localTarget) {
      const restored = {
        translations: JSON.parse(JSON.stringify(localTarget.translations)),
        media: JSON.parse(JSON.stringify(localTarget.media)),
      };
      setContent(restored);
      ["tr", "en", "de"].forEach((lng) => {
        if (restored.translations[lng]) {
          i18n.addResourceBundle(lng, "translation", restored.translations[lng], true, true);
        }
      });
      i18n.changeLanguage(i18n.language);
      reloadPreview();
      return;
    }
    const fresh = await fetchContent();
    setContent(fresh);
    await refresh();
    reloadPreview();
  };

  const sectionComp = useMemo(() => {
    if (!content) return null;
    switch (section) {
      case "general": return <GeneralSection content={content} lang={lang} setField={setField} />;
      case "home": return <HomeSection content={content} lang={lang} setField={setField} />;
      case "tech": return <TechSection content={content} lang={lang} setField={setField} />;
      case "hardware": return <HardwareSection content={content} lang={lang} setField={setField} />;
      case "solutions": return <SolutionsSection content={content} lang={lang} setField={setField} />;
      case "whyus": return <WhyUsSection content={content} lang={lang} setField={setField} />;
      case "contact": return <ContactSection content={content} lang={lang} setField={setField} />;
      case "seo": return <SeoSection content={content} lang={lang} setField={setField} />;
      case "media": return <MediaSection content={content} setField={setField} />;
      case "history": return <HistorySection onRestored={onRestored} />;
      default: return null;
    }
  }, [content, section, lang]);

  if (!content) {
    return (
      <div className="min-h-screen bg-[#f8faf9] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#2b8a7e]" />
      </div>
    );
  }

  const currentBase = process.env.REACT_APP_BACKEND_URL || "";
  const previewSrc = `${currentBase}${previewRoute}?_pv=${previewKey}`;

  return (
    <div className="min-h-screen bg-[#f8faf9]" data-testid="admin-dashboard">
      <Toaster position="top-right" richColors />

      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#f8faf9]/90 backdrop-blur border-b border-[#1a3636]/10">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#1a3636] text-[#f8faf9] flex items-center justify-center font-display font-extrabold">P</div>
            <div>
              <p className="font-display font-extrabold text-[#1a3636] leading-none">
                Pergamon <span className="text-[#2b8a7e]">Admin</span>
              </p>
              <p className="text-[10px] uppercase tracking-widest text-[#1a3636]/50">
                {user?.email} · İçerik Yönetimi
                {isMockMode() && (
                  <span
                    data-testid="admin-demo-badge"
                    className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 tracking-wider"
                  >
                    Demo Modu
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {["general","home","tech","hardware","solutions","whyus","contact","seo"].includes(section) && (
              <div className="flex rounded-full bg-white border border-[#1a3636]/10 p-1" data-testid="admin-lang-tabs">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    data-testid={`admin-lang-${l.code}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium tracking-wide transition ${
                      lang === l.code ? "bg-[#1a3636] text-[#f8faf9]" : "text-[#1a3636]/70 hover:text-[#1a3636]"
                    }`}
                  >
                    {l.code.toUpperCase()}
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowPreview((v) => !v)}
              data-testid="admin-preview-toggle"
              className="inline-flex items-center gap-1.5 text-sm border border-[#1a3636]/15 text-[#1a3636] px-3 py-2 rounded-full hover:bg-[#1a3636]/5 transition"
              title={showPreview ? "Önizlemeyi Gizle" : "Önizlemeyi Göster"}
            >
              {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
              Önizleme
            </button>

            <button
              onClick={doReset}
              disabled={busy}
              data-testid="admin-reset"
              className="inline-flex items-center gap-1.5 text-sm border border-[#1a3636]/15 text-[#1a3636] px-3 py-2 rounded-full hover:bg-[#1a3636]/5 transition disabled:opacity-50"
            >
              <RotateCcw size={14} /> Sıfırla
            </button>

            <button
              onClick={save}
              disabled={busy}
              data-testid="admin-save"
              className="inline-flex items-center gap-2 bg-[#1a3636] text-[#f8faf9] px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#2b8a7e] transition disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Kaydet
            </button>

            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 text-sm text-[#1a3636]/60 hover:text-red-600 px-2 py-2"
              title="Çıkış"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <div className={`max-w-[1600px] mx-auto px-6 py-6 grid gap-6 ${showPreview ? "grid-cols-1 xl:grid-cols-12" : "grid-cols-1 lg:grid-cols-12"}`}>
        {/* Sidebar */}
        <aside className={showPreview ? "xl:col-span-2" : "lg:col-span-3"}>
          <nav className="bg-white rounded-2xl border border-[#1a3636]/5 p-2 shadow-[0_8px_30px_rgb(26,54,54,0.04)] sticky top-24">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const active = section === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setSection(s.id)}
                  data-testid={`admin-section-${s.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm font-medium transition ${
                    active ? "bg-[#2b8a7e]/10 text-[#1a3636]" : "text-[#1a3636]/70 hover:bg-[#f8faf9]"
                  }`}
                >
                  <Icon size={17} strokeWidth={1.5} className={active ? "text-[#2b8a7e]" : ""} />
                  {s.label}
                </button>
              );
            })}
            {savedAt && (
              <p className="mt-4 text-xs text-[#1a3636]/50 px-4 py-2">
                Son kayıt: {savedAt.toLocaleTimeString("tr-TR")}
              </p>
            )}
          </nav>
        </aside>

        {/* Editor */}
        <main className={showPreview ? "xl:col-span-5" : "lg:col-span-9"}>
          <div className="bg-white rounded-2xl border border-[#1a3636]/5 p-6 md:p-8 shadow-[0_8px_30px_rgb(26,54,54,0.04)]">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-[#1a3636] tracking-tight">
                {SECTIONS.find((s) => s.id === section)?.label}
              </h2>
              {["general","home","tech","hardware","solutions","whyus","contact","seo"].includes(section) && (
                <span className="text-xs uppercase tracking-widest text-[#2b8a7e]">
                  Dil: {lang.toUpperCase()}
                </span>
              )}
            </div>
            {sectionComp}
          </div>
        </main>

        {/* Live preview iframe */}
        {showPreview && (
          <aside className="xl:col-span-5" data-testid="admin-preview-panel">
            <div className="bg-white rounded-2xl border border-[#1a3636]/5 p-3 shadow-[0_8px_30px_rgb(26,54,54,0.04)] sticky top-24">
              <div className="flex items-center justify-between gap-2 mb-3 px-2">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                  </div>
                  <div className="ml-3 flex gap-1 text-xs">
                    {[
                      { r: "/", label: "Ana" },
                      { r: "/teknoloji", label: "Teknoloji" },
                      { r: "/donanim", label: "Donanım" },
                      { r: "/cozumler", label: "Çözümler" },
                      { r: "/neden-biz", label: "Neden Biz?" },
                      { r: "/iletisim", label: "İletişim" },
                    ].map((b) => (
                      <button
                        key={b.r}
                        onClick={() => { setPreviewRoute(b.r); reloadPreview(); }}
                        data-testid={`preview-nav-${b.r === "/" ? "home" : b.r.slice(1)}`}
                        className={`px-2 py-1 rounded-md transition ${
                          previewRoute === b.r ? "bg-[#1a3636] text-white" : "text-[#1a3636]/60 hover:text-[#1a3636] bg-[#f8faf9]"
                        }`}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  onClick={reloadPreview}
                  data-testid="admin-preview-reload"
                  className="inline-flex items-center gap-1 text-xs text-[#1a3636]/70 hover:text-[#2b8a7e] px-2 py-1 rounded-md"
                  title="Yenile"
                >
                  <RefreshCw size={13} />
                </button>
              </div>
              <iframe
                ref={iframeRef}
                key={previewKey}
                src={previewSrc}
                title="Canlı Önizleme"
                data-testid="admin-preview-iframe"
                className="w-full h-[calc(100vh-190px)] min-h-[520px] rounded-xl border border-[#1a3636]/10 bg-white"
              />
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
